import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Users, Loader2, ShieldCheck, Eye, AlertCircle, Shield } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useAccountStore } from '../store/accountStore';
import { AuthShell, AuthCard } from '../components/Common/AuthShell';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

type Role = 'editor' | 'viewer';

interface InvitationInfo {
  id: string;
  role: Role;
  email: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  emailMatches: boolean;
  owner: { id: string; name: string; email: string | null };
}

function authHeaders(json = false): HeadersInit {
  const token = localStorage.getItem('token');
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

export const AcceptInvite: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setActiveAccount = useAccountStore((s) => s.setActiveAccount);
  const loadAccounts = useAccountStore((s) => s.loadAccounts);

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bounce unauthenticated users to login, preserving the invite link so
  // they land back here after signing in (or registering) with the
  // invited email address.
  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError(t('team.noToken', { defaultValue: 'This invitation link is invalid.' }));
      return;
    }
    if (!isAuthenticated) {
      const returnTo = `/accept-invite?token=${encodeURIComponent(token)}`;
      navigate(`/login?redirect=${encodeURIComponent(returnTo)}`, { replace: true });
    }
  }, [token, isAuthenticated, navigate, t]);

  const lookup = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/team/invitations/lookup?token=${encodeURIComponent(token)}`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || t('team.lookupFailed', { defaultValue: 'Could not load this invitation.' }));
        return;
      }
      setInfo(data.invitation);
    } catch (err) {
      console.error('[AcceptInvite] lookup failed:', err);
      setError(t('team.lookupFailed', { defaultValue: 'Could not load this invitation.' }));
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, t]);

  useEffect(() => {
    void lookup();
  }, [lookup]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await fetch(`${API_URL}/team/invitations/accept`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ token }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || data.error || t('team.acceptFailed', { defaultValue: 'Failed to accept invitation' }));
        return;
      }
      toast.success(t('team.joined', { defaultValue: 'You joined the workspace' }));
      await loadAccounts();
      if (data?.account?.id) setActiveAccount(data.account.id);
      window.location.assign('/dashboard');
    } catch (err) {
      console.error('[AcceptInvite] accept failed:', err);
      toast.error(t('team.acceptFailed', { defaultValue: 'Failed to accept invitation' }));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <AuthShell hideFooter>
      <AuthCard>
        <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-cyan-300 to-violet-500 shadow-glow">
          <Users className="h-7 w-7 text-growth-ink" />
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-6 text-white/50">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center">
            <AlertCircle className="mx-auto mb-3 h-8 w-8 text-rose-400" />
            <p className="mb-6 text-white/75">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="rounded-2xl border border-white/14 bg-white/[0.06] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-white/[0.1]"
            >
              {t('nav.dashboard', { defaultValue: 'Go to dashboard' })}
            </button>
          </div>
        ) : info ? (
          <div className="text-center">
            <h1 className="mb-1 text-xl font-black text-white">
              {t('team.inviteHeading', { defaultValue: 'Join {{name}}', name: info.owner.name })}
            </h1>
            <p className="mb-5 text-sm text-white/[0.6]">
              {t('team.inviteSub', {
                defaultValue: '{{name}} invited you to collaborate on their workspace.',
                name: info.owner.name,
              })}
            </p>

            <div className="mb-6 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-1.5">
              {info.role === 'viewer' ? (
                <>
                  <Eye className="h-4 w-4 text-cyan-200" />
                  <span className="text-sm font-semibold text-cyan-100">
                    {t('team.viewerReadOnly', { defaultValue: 'Viewer (read-only)' })}
                  </span>
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 text-lime-300" />
                  <span className="text-sm font-semibold text-lime-200">
                    {t('team.editorCanEdit', { defaultValue: 'Editor (can create & edit)' })}
                  </span>
                </>
              )}
            </div>

            {info.status !== 'pending' ? (
              <div className="rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-200">
                {info.status === 'accepted'
                  ? t('team.alreadyAccepted', { defaultValue: 'This invitation has already been accepted.' })
                  : info.status === 'expired'
                  ? t('team.inviteExpired', { defaultValue: 'This invitation has expired.' })
                  : t('team.inviteRevokedMsg', { defaultValue: 'This invitation is no longer valid.' })}
              </div>
            ) : !info.emailMatches ? (
              <div className="rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-200">
                {t('team.emailMismatch', {
                  defaultValue: 'This invitation was sent to {{email}}. Please log in with that email to accept.',
                  email: info.email,
                })}
              </div>
            ) : (
              <button
                type="button"
                onClick={handleAccept}
                disabled={accepting}
                className="landing-primary-cta w-full disabled:cursor-not-allowed disabled:opacity-60"
              >
                {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                {t('team.acceptInvitation', { defaultValue: 'Accept invitation' })}
              </button>
            )}
          </div>
        ) : null}
      </AuthCard>
    </AuthShell>
  );
};

export default AcceptInvite;
